import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapTrifold, ShieldCheck } from "@phosphor-icons/react";

/**
 * RGPD-compliant opt-in card for daily route tracking.
 * The tech explicitly toggles it on; only then does the app record pings to
 * `location_pings`. Retention is 7 days server-side (TTL index).
 */
export default function TrackingConsentCard() {
  const { user, refresh } = useAuth();
  const enabled = user?.location_tracking_enabled === true;
  const [saving, setSaving] = useState(false);

  const toggle = async (v) => {
    setSaving(true);
    try {
      await api.patch("/users/me/tracking-consent", { enabled: v });
      toast.success(v ? "Tracé journalier activé" : "Tracé journalier désactivé");
      if (typeof refresh === "function") await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="tracking-consent-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <MapTrifold size={18} weight="duotone" className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm">Tracé journalier</div>
            <Switch
              checked={enabled}
              onCheckedChange={toggle}
              disabled={saving}
              data-testid="tracking-consent-switch"
            />
          </div>
          <div className="text-[11px] text-white/50 mt-1 leading-relaxed">
            Enregistre ta position toutes les 5 min (ou tous les 50 m) pendant tes heures de travail
            (Lun-Ven 7h-19h) pour permettre au gérant de consulter ton itinéraire. Les données sont
            supprimées automatiquement au bout de 7 jours.
          </div>
          <div className="text-[10px] text-white/40 mt-2 flex items-center gap-1">
            <ShieldCheck size={11} weight="fill" className="text-emerald-400" />
            Tu peux désactiver à tout moment. Conforme RGPD.
          </div>
        </div>
      </div>
    </div>
  );
}
